import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ShieldCheck, Lock, FileCheck, Scale } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const securityFeatures = [
  {
    icon: FileCheck,
    title: 'Smart Contract Audits',
    description: 'All smart contracts audited by leading security firms including CertiK and SlowMist.',
  },
  {
    icon: ShieldCheck,
    title: 'PoA Validator System',
    description: 'Vetted validators ensure network integrity with stake-based accountability.',
  },
  {
    icon: Lock,
    title: 'Data Encryption',
    description: 'Military-grade encryption protects all user data and transaction details.',
  },
  {
    icon: Scale,
    title: 'Compliance Ready',
    description: 'Built with regulatory compliance in mind for seamless enterprise adoption.',
  },
];

const trustBadges = [
  { name: 'CertiK Audited', icon: '🛡️' },
  { name: 'SOC 2 Type II', icon: '✓' },
  { name: 'ISO 27001', icon: '🔒' },
  { name: 'Bug Bounty', icon: '🎯' },
];

export const Security = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="security"
      className={`py-24 sm:py-32 relative ${isDark ? 'bg-[#050505]' : 'bg-[#F8FAFC]'}`}
      ref={ref}
      data-testid="security-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-blue-500 mb-4 block">
            Security & Trust
          </span>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Built for <span className="gradient-text">Enterprise Security</span>
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
            Your assets are protected by industry-leading security measures and best practices.
          </p>
        </motion.div>

        {/* Security Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {securityFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className={`flex gap-6 p-8 rounded-3xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                isDark 
                  ? 'bg-white/[0.03] border-white/10' 
                  : 'bg-white border-black/5'
              }`}
            >
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <feature.icon className="w-7 h-7 text-blue-400" />
                </div>
              </div>
              <div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {feature.title}
                </h3>
                <p className={isDark ? 'text-zinc-400' : 'text-slate-600'}>
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="flex flex-wrap justify-center gap-4"
        >
          {trustBadges.map((badge) => (
            <div
              key={badge.name}
              className={`flex items-center gap-3 px-6 py-3 rounded-full border ${
                isDark 
                  ? 'bg-white/[0.05] border-white/10' 
                  : 'bg-white border-black/5'
              }`}
            >
              <span className="text-xl">{badge.icon}</span>
              <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{badge.name}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
